const en = {
  common: {
    appName: "OELOX Partner",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    clear: "Clear",
    retry: "Try again",
    loading: "Loading…",
    create: "Create",
    refresh: "Refresh",
    search: "Search",
    online: "Online",
    busy: "Busy",
    offline: "Offline",
    active: "Active",
    enabled: "Enabled",
    disabled: "Disabled",
    manual: "Manual",
    pickup: "Pickup",
    delivery: "Delivery",
    unassigned: "Unassigned",
    dash: "—",
    copy: "Copy",
    copied: "Copied.",
    copyFailed: "Could not copy.",
  },
  language: {
    select: "Select language",
    title: "Language",
    hint: "Choose Arabic, Kurdish, or English for the partner workspace.",
  },
  auth: {
    tag: "PARTNER",
    loginTitle: "Partner sign in",
    loginSubtitle:
      "Sign in to your partner workspace. Partner accounts only — not business or OELOX admin.",
    email: "Email",
    loginId: "Username or email",
    emailPlaceholder: "username or you@partner.com",
    password: "Password",
    passwordPlaceholder: "Your password",
    signIn: "Sign in",
    signingIn: "Signing in…",
    foot: "Secure partner workspace · OELOX",
    hidePassword: "Hide password",
    showPassword: "Show password",
    authFailed: "Authentication failed.",
    notPartner: "This account is not linked to a partner workspace.",
    signInFailed: "Could not sign in. Please try again.",
    invalidCredentials: "Incorrect username or password.",
    emailNotConfirmed: "Please confirm your email address before signing in.",
    tooMany: "Too many attempts. Please try again in a moment.",
    networkError: "Connection failed. Check your internet and try again.",
    signedIn: "Signed in.",
    changePasswordTitle: "Set a new password",
    changePasswordSubtitle:
      "Your account requires a password change before you can open the partner workspace.",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    updatePassword: "Update password",
    updatingPassword: "Updating…",
    passwordTooShort: "Password must be at least 8 characters.",
    passwordMismatch: "Passwords do not match.",
    passwordUpdateFailed: "Could not update password. Please try again.",
    newPartnerLink: "New partner? Activate with your passcode",
    passcodeTitle: "New partner",
    passcodeSubtitle:
      "Enter the one-time passcode from OELOX admin to activate your partner account.",
    passcodeLabel: "Passcode",
    passcodePlaceholder: "6-digit code",
    continue: "Continue",
    checkingCode: "Checking…",
    back: "Back",
    backToSignIn: "Back to sign in",
    passcodeInvalid: "Invalid passcode. Check and try again.",
    passcodeUsed: "This passcode was already used.",
    passcodeExpired: "This passcode has expired. Request a new one.",
    passcodeAttempts: "Too many attempts. Request a new passcode.",
    onboardingTitle: "Complete setup",
    onboardingSubtitle: "Create your login to activate the partner account.",
    displayName: "Your name",
    username: "Username",
    usernameHint: "3–30 chars: a-z 0-9 . _ - — this is your login ID",
    usernameInvalid: "Invalid username (3–30 chars: a-z 0-9 . _ -).",
    usernameTaken: "That username is already taken. Try another one.",
    emailOptional: "Email (optional)",
    emailHint: "name@example.com",
    emailInvalid: "Enter a valid email or leave it empty.",
    emailTaken: "Email is already registered.",
    activateAccount: "Activate account",
    activatingAccount: "Activating…",
    setupComplete: "Account activated. Sign in to continue.",
    partnerActivated: "Account activated — welcome to OELOX Partner!",
    onboardingFailed: "Setup failed. Try again.",
  },
  nav: {
    dashboard: "Dashboard",
    orders: "Orders",
    drivers: "Drivers",
    clients: "Clients",
    analytics: "Analytics",
    wallet: "Wallet",
    settings: "Settings",
  },
  shell: {
    openMenu: "Open menu",
    notifications: "Notifications",
    settings: "Settings",
    signOut: "Sign out",
    workspaceFallback: "Partner",
  },
  overview: {
    eyebrow: "Command center",
    titleFallback: "Partner",
    subtitle: "See what's happening now — orders, drivers, and clients at a glance.",
    loadError: "Could not load live orders. Check your session and try again.",
    updatedLive: "Updated live",
    date: {
      ariaGroup: "Date range",
      today: "Today",
      custom: "Custom",
      showing: "Showing",
      pickerEyebrow: "Date range",
      pickerTitle: "Custom period",
      pickerSubtitle: "Filter dashboard activity by date.",
      presets: "Presets",
      presetYesterday: "Yesterday",
      presetLast7: "Last 7 days",
      presetLast30: "Last 30 days",
      presetThisMonth: "This month",
      from: "From",
      to: "To",
      confirm: "Apply",
    },
    flow: {
      title: "Live order flow",
      liveBadge: "Live",
      hint: "Tap a step to filter orders below",
      activeNow: "active now",
      ordersLabel: "orders",
      aria: "Live order flow",
    },
    recentOrders: {
      title: "Recent orders",
      subtitle: "Last orders — tap a row to open it in Orders.",
      empty: "No orders in this view",
      filteredBy: "Filtered by",
    },
    activeClients: {
      title: "Active clients",
      subtitle: "Latest activity across your clients.",
      viewAll: "View all",
      empty: "No client activity yet",
      ordersCount: "{count} orders",
      openCount: "{count} open",
      justNow: "just now",
      noOrders: "No orders yet",
    },
    dispatch: {
      aria: "Dispatch intelligence",
      driverStatus: "Driver status",
      driverCaption: "Online {online} · Busy {busy} · Offline {offline}",
      activeLoad: "Active load",
      loadCaption: "In delivery {inDelivery} · Unassigned {unassigned}",
      fallback: "OELOX fallback",
      fallbackCaption: "Read-only · Used today —",
      wallet: "Wallet balance",
      walletCaption: "Credit limit {limit}",
      walletLow: "Low — top up soon",
      walletBlocked: "Below limit — dispatch paused",
    },
  },
  orders: {
    title: "Orders",
    eyebrow: "Operations center",
    subtitle:
      "Follow incoming orders, accept them and send them to drivers — updates automatically.",
    liveTitle: "Live orders",
    searchPlaceholder: "Search client, guest, phone…",
    searchAria: "Search orders",
    emptyTitle: "No orders in this stage",
    emptyHint: "Only orders from your partner clients are listed.",
    emptyListening:
      "New orders appear here automatically — keep this screen open.",
    emptyTitleStage: "No {stage} orders right now",
    emptyRangeHint:
      "Looking for older orders? Change the date range from the top bar.",
    showAllOrders: "Show all orders",
    backToList: "Back to orders",
    stagesAria: "Order stages",
    stages: {
      pending: "Pending",
      processing: "Preparation",
      ready: "Ready",
      in_delivery: "Delivery",
      completed: "Completed",
      cancelled: "Cancelled",
      all: "All",
    },
    stagesShort: {
      pending: "Pending",
      processing: "Preparing",
      ready: "Ready",
      in_delivery: "Delivery",
      completed: "Done",
      cancelled: "Cancelled",
      all: "All",
    },
    method: {
      whatsappAi: "WhatsApp AI",
      whatsapp: "WhatsApp",
      manual: "Manual",
      phone: "Phone",
      webStore: "Web Store",
      pos: "POS",
      marketplace: "Marketplace",
    },
    kpis: {
      processing: "Processing",
      ready: "Ready",
      inDelivery: "In delivery",
    },
    updatedNow: "Updated just now",
    updatedAgo: "Updated {value}",
    showing: "Showing {shown} of {total}",
    sort: {
      label: "Sort orders",
      newest: "Newest first",
      oldest: "Oldest first",
      amount: "Highest total",
    },
    waitNow: "now",
    accept: "Accept order",
    acceptShort: "Accept",
    decline: "Decline order",
    declineConfirm:
      "Decline this order? It leaves your desk and the store is notified.",
    declineReasonPrompt: "Reason (optional)",
    declineModal: {
      title: "Decline order",
      subtitle: "The order leaves your desk and the store is notified.",
      reasonLabel: "Reason",
      reasons: {
        busy: "Too busy right now",
        far: "Too far / out of range",
        closed: "Closing or unavailable",
        other: "Other",
      },
      noteLabel: "Note (optional)",
      notePlaceholder: "Short note for the store…",
      confirm: "Decline order",
    },
    assign: "Assign driver",
    assignShort: "Assign",
    changeShort: "Change",
    needsDriver: "Needs a driver",
    detailEmpty: "Select an order",
    detailEmptyHint: "Click any order in the list to see its details here.",
    items: "Items",
    notes: "Notes",
    detail: {
      customer: "Customer",
      timeline: "Timeline",
      created: "Created",
      accepted: "Accepted",
      copyNumber: "Copy order number",
      returnToStore: "Rejected · return to store",
      copyPhone: "Copy phone number",
      call: "Call customer",
      openMap: "Open in maps",
      copyAddress: "Copy address",
      more: "More",
      changeDriver: "Change driver",
      noDriverAssigned: "No driver assigned yet.",
      next: {
        pending: "Next: accept the order, then choose a delivery driver.",
        driver: "Next: assign a driver to deliver this order.",
        onTheWay: "Order is with the driver — track the delivery.",
      },
      itemsCount: "{count} items",
      km: "{value} km",
      distance: "Distance",
      payment: "Payment",
      paymentType: {
        prepaid: "Prepaid",
        cod: "Cash on delivery",
        payAtCashier: "Pay at cashier",
        unknown: "Payment unknown",
      },
      method: "Method",
      methodCash: "Cash",
      methodCard: "Card",
      methodWallet: "Wallet",
      methodCod: "Cash on delivery",
      statusLabel: "Status",
      statusPaid: "Paid",
      statusUnpaid: "Unpaid",
      statusCodCollected: "Cash collected",
      statusPickupCollected: "Paid at pickup",
      statusFailed: "Payment failed",
      statusRefunded: "Refunded",
      cashCollector: "Cash collector",
      driverCollects: "Driver collects cash",
      deliveryFee: "Delivery fee",
      feePayerCustomer: "Customer pays",
      feePayerRestaurant: "Restaurant pays",
      feePayerOelox: "OELOX pays",
      feePayerPartner: "Partner pays",
      feePayerFree: "Free delivery",
      clientCharge: "Client charge",
      partnerFeePerOrder: "per order",
      partnerFeePercentage: "percentage",
      prep: "Prep",
      prepMinutes: "~{minutes} min",
      placed: "Placed",
      pickupReady: "Ready for pickup",
      total: "Total",
    },
    assignSection: "Assign driver",
    requestOelox: "Request OELOX Drivers",
    reassign: "Reassign",
    clearDriver: "Clear driver",
    noDrivers: "No partner drivers yet. Add drivers, or request OELOX fallback.",
    acceptModal: {
      title: "Accept order",
      dispatchTitle: "Dispatch · {business}",
      partnerDrivers: "Partner drivers",
      steps: "1) Accept the order · 2) Choose a driver below — or send to all.",
      selectDriversHint:
        "Select one driver to assign, or multiple to broadcast.",
      sendToAll: "Send to all available & busy drivers",
      sendToAllHint: "Any eligible partner driver can accept this delivery.",
      selectOeloxGlobal: "Select OELOX Global drivers",
      oeloxNoDrivers: "No OELOX global drivers are online right now.",
      requestAnyOelox: "Let OELOX pick a driver instead",
      oeloxOnline: "Online",
      oeloxOffline: "Offline",
      assignDriver: "Assign driver",
      blastSelected: "Broadcast to selected drivers",
      dispatch: "Dispatch",
      acceptOnly: "Accept only",
      acceptAndAssign: "Accept & assign driver",
      searchDrivers: "Search drivers…",
      noDriverMatch: "No drivers match your search.",
      selectedCount: "{count} selected",
      bypassModeHint:
        "Bypass mode auto-broadcasts on accept. Switch to manual dispatch to pick drivers.",
      noPartnerDrivers: "No partner drivers in your fleet.",
      noAssignableDrivers: "No online or busy partner drivers right now.",
    },
    toast: {
      accepted: "Order accepted.",
      acceptedNoDrivers: "Order accepted — no partner drivers available.",
      acceptedBlast: "Order accepted and blasted to your drivers.",
      acceptedDispatched: "Order accepted and driver notified.",
      dispatched: "Drivers notified.",
      assigned: "Driver assigned.",
      reassigned: "Driver reassigned.",
      cleared: "Driver cleared.",
      oeloxRequested: "OELOX driver requested.",
      oeloxAssigned: "OELOX driver assigned.",
      declined: "Order declined.",
      newOrder: "New order received",
      view: "View",
    },
  },
  drivers: {
    title: "Drivers",
    searchPlaceholder: "Search drivers…",
    searchAria: "Search drivers",
    add: "Add driver",
    refresh: "Refresh",
    emptyTitle: "No drivers yet",
    emptyHint: "Create partner-fleet drivers to assign deliveries.",
    createTitle: "Add driver",
    name: "Name",
    namePlaceholder: "Driver name",
    phone: "Phone",
    vehicleType: "Vehicle type",
    vehicleNumber: "Vehicle number",
    bicycle: "Bicycle",
    motorcycle: "Motorcycle",
    car: "Car",
    creating: "Creating…",
    create: "Create",
    modePasscode: "Add by passcode",
    modeReady: "Ready to use",
    passcodeHint:
      "Creates a partner-fleet driver with a one-time passcode to activate in the driver app.",
    readyHint:
      "Creates the driver account now with the login below. Hand the credentials over once.",
    address: "Address",
    username: "Username",
    usernameHint: "3–30 chars: a-z, 0-9, dot, dash, underscore.",
    createReady: "Create account",
    readyCredentialsHint:
      "The driver can sign in to the driver app now with these credentials.",
    resetPassword: "Reset password",
    deactivate: "Deactivate",
    passcode: "Passcode",
    modeLabel: "Driver type",
    mode: {
      own: "Staff",
      paid: "Contractor",
    },
    modeOwnHint: "Staff \u2014 on your payroll; vehicle on you; no per-trip pay.",
    modePaidHint: "Contractor \u2014 independent; his own vehicle; paid per trip via his wallet.",
    modeToastChanged: "Driver type updated.",
    tripPayTitle: "Pay per trip",
    tripPayLabel: "Driver pay per trip (IQD)",
    tripPayPlaceholder: "e.g. 2000",
    tripPayUsesDefault: "Partner default: {amount} — leave empty to use it.",
    tripPayHint: "Leave empty to use the partner default rate.",
    tripPaySaved: "Driver pay updated.",
    toast: {
      created: "Driver created.",
      deactivated: "Driver deactivated.",
      activated: "Driver activated.",
      updated: "Driver updated.",
    },
    presence: {
      online: "Online",
      busy: "Busy",
      offline: "Offline",
    },
    subtitle: "Your partner fleet — live presence, pay and wallets.",
    columns: {
      name: "Driver",
      status: "Status",
      vehicle: "Vehicle",
      activeOrders: "Active orders",
      mode: "Mode",
      pay: "Trip pay",
      wallet: "Wallet",
    },
    filterAll: "All statuses",
    payDefaultMark: "default",
    back: "Back to drivers",
    notFound: "Driver not found",
    tabs: {
      settings: "Driver settings",
      reports: "Reports",
      operations: "Operations",
      overview: "Overview",
      wallet: "Wallet",
      payouts: "Payouts",
      mode: "Mode",
    },
    tabsAria: "Driver sections",
    deactivateTitle: "Deactivate driver?",
    deactivateBody:
      "They stop receiving orders and are removed from dispatch lists.",
    inactive: "Inactive",
    activate: "Activate",
    whatsapp: "WhatsApp",
    editNotAvailable: "Read-only for now — editing requires backend support.",
    missingFields:
      "Not in the backend yet: driver address, national ID & extra documents.",
    vehicleModel: "Vehicle model",
    vehicleColor: "Colour",
    vehicleYear: "Year",
    documents: "Documents",
    licenseImage: "Driving licence image",
    selfPhoto: "Self photo",
    open: "Open",
    invalidYear: "Enter a valid year (1980–2100).",
    assignExisting: {
      cta: "Add registered",
      title: "Add a driver who already has an account",
      subtitle:
        "Find him by phone. If he already serves client businesses they will be unlinked after you confirm — live deliveries block this.",
      search: "Find",
      notFound: "No driver with this phone number.",
      ownedByYou: "This driver is already in your fleet.",
      ownedOther: "This driver already belongs to another partner.",
      sharedDriver: "Shared driver",
      clientsLabel: "Currently linked to:",
      unlinkNote: "Confirming will unlink him from these clients and add him to your fleet.",
      move: "Add to my fleet",
      moveWithUnlink: "Unlink & add to my fleet",
      moved: "Driver added to your fleet.",
      movedWithClients: "Driver added to your fleet — unlinked from: {clients}",
      alreadyYours: "Driver already in your fleet.",
    },
    release: {
      title: "Release from fleet",
      hint: "Remove this driver from your fleet. He becomes unassigned and can rejoin later via passcode or another partner.",
      cta: "Release",
      toast: "Driver released from your fleet.",
    },
    reports: {
      orderNo: "Order",
      client: "Client",
      ordersHistory: "Orders history",
      noOrders: "No orders for this driver yet",
      currentOrders: "Current orders",
      tripHistory: "Trip history",
      noTrips: "No completed trips yet",
      noCurrentOrders: "No active orders assigned",
      limitNote: "Latest 25 trips",
    },
  },
  clients: {
    title: "Clients",
    searchPlaceholder: "Search businesses…",
    searchAria: "Search clients",
    add: "Add client",
    refresh: "Refresh",
    emptyTitle: "No partner clients yet",
    emptyHint: "Identity SoT or legacy linked businesses will appear here.",
    createTitle: "Add client",
    businessName: "Business name",
    phone: "Phone",
    address: "Address",
    addressPlaceholder: "Street, city…",
    emailOptional: "Email (optional — for invoices)",
    username: "Username (client login ID)",
    usernameHint:
      "3–30 chars: a-z, 0-9, dash only. The client logs in with this — not email.",
    edit: "Edit client",
    editTitle: "Edit client",
    editHint:
      "Username is the client's login ID. Email is optional — used for invoices.",
    delete: "Delete",
    deleteTitle: "Delete client?",
    deleteBody:
      "This soft-deletes the client: status becomes cancelled and any active setup passcodes are revoked. History stays in the system.",
    deleteConfirmCta: "Yes, delete {name}",
    save: "Save changes",
    creating: "Creating…",
    create: "Create + passcode",
    modePasscode: "Add by passcode",
    modeReady: "Ready to use",
    readyHint:
      "Creates the store account now with the login below. Hand the credentials over once — the owner can change the password later.",
    slug: "Public store slug",
    slugHint: "Public link: /store/<slug>",
    createReady: "Create account",
    readyCredentialsHint:
      "The store can sign in now. Its storefront stays hidden until the owner enables it in Settings.",
    passcodeTitle: "Business passcode",
    passcodeHint:
      "Share once so the business can activate. Identity is set to partner + your controlling_partner_id.",
    dismiss: "Dismiss",
    reissue: "Passcode",
    passcodeModalHint:
      "Single use — expires in ~2 hours. Share it with the business to activate their account.",
    openOrders: "Open orders",
    active: "Active",
    inactive: "Inactive",
    unknownStatus: "Unknown",
    openOrdersKpi: "Open orders",
    noActivity: "No activity yet",
    driverPrefix: "Driver: {name}",
    toast: {
      created: "Client created as Partner Client.",
      updated: "Client updated.",
      deleted: "Client deleted — moved to cancelled.",
    },
    eyebrow: "Client book",
    subtitle:
      "Your partner-managed businesses — orders, delivery fees and what they owe.",
    totalKpi: "Clients",
    trial: "Trial",
    onboarding: "Onboarding",
    filterAll: "All",
    filtersAria: "Client status filters",
    colClient: "Client",
    colStatus: "Status",
    colOrders: "Orders",
    colCompleted: "Completed",
    colFees: "Delivery fees",
    colCharges: "Service charges",
    colOutstanding: "Outstanding",
    colLastOrder: "Last order",
    colOpen: "open",
    view: "View",
    note: "Orders, fees and charges are lifetime totals; outstanding shows the unsettled balance.",
  },
  clientView: {
    back: "Back to clients",
    notFound: "Client not found.",
    tabsAria: "Client report sections",
    tabs: {
      overview: "Overview",
      orders: "Orders",
      statement: "Fees & statement",
      drivers: "Drivers",
    },
    ordersRange: "Orders (range)",
    activeNow: "Active now",
    deliveryIncome: "Delivery income",
    clientCharges: "Service charges",
    outstanding: "Outstanding",
    driversNow: "Active drivers",
    businessInfo: "Business info",
    login: "Login ID",
    ownerEmail: "Owner email",
    linkedAt: "Linked",
    dispatchAuthority: "Dispatch authority",
    currency: "Currency",
    terms: "Commercial terms",
    termsHint: "Commercial terms are set by the OELOX admin team.",
    noOrdersRange: "No orders in this range.",
    legacyNote:
      "{count} completed deliveries have no priced delivery fee (legacy data).",
  },
  clientOrders: {
    searchPlaceholder: "Search orders…",
    searchAria: "Search client orders",
    stageFilter: "Order stage",
    fulfillmentFilter: "Order type",
    export: "Export CSV",
    colDate: "Date",
    colOrder: "Order",
    colCustomer: "Customer",
    colDriver: "Driver",
    colStatus: "Status",
    colTotal: "Total",
    colDeliveryFee: "Delivery fee",
    colResponsibility: "Fee payer",
    colCharge: "Charge",
    colPayment: "Payment",
    colKm: "KM",
    showing: "Showing {from}–{to} of {total}",
    prev: "Previous",
    next: "Next",
  },
  statement: {
    charged: "Charged",
    settled: "Settled",
    pendingInRange: "Pending (range)",
    outstanding: "Outstanding",
    cancelledFees: "{count} cancelled orders with a fee",
    feeMix: "Delivery fee payer mix",
    summary: "Summary",
    instantTotal: "Instant fee total",
    voidedTotal: "Voided this range",
    wallet: "Wallet",
    walletBalance: "Wallet balance",
    walletPending: "Pending credit",
    serviceFeeNote:
      "Service fees are recorded per order; settle credit entries to clear pending balances.",
    ledger: "Ledger entries",
    empty: "No charges in this range.",
    emptyLedger: "No charges recorded yet.",
    recentLedger: "Recent charges",
    settle: "Settle",
    settleToast: "Entry settled.",
    settleFailed: "Could not settle entry.",
    colDate: "Date",
    colOrder: "Order",
    colType: "Type",
    colOrderTotal: "Order total",
    colFee: "Fee",
    colMode: "Mode",
    colStatus: "Status",
    colPayer: "Payer",
    colOrders: "Orders",
    colFees: "Fees",
  },
  wallet: {
    title: "Driver wallet",
    owesDriver: "Partner owes the driver",
    driverOwes: "Driver owes the partner",
    settled: "Settled — nothing owed",
    empty: "No wallet entries yet.",
    amountPlaceholder: "Amount (+ or −)",
    notePlaceholder: "Note (optional)",
    addEntry: "Add entry",
    entryHint:
      "+ means you owe the driver more · − means the driver owes you (e.g. cash collected).",
    toastEntryAdded: "Wallet entry added.",
    type: {
      trip_fee: "Delivery fee",
      cod_collected: "Cash collected from customer",
      adjustment: "Manual entry",
      topup: "Wallet top-up",
      trip_charge: "Trip charge",
    },
  },
  clientWallet: {
    title: "Client wallet",
    balance: "Balance",
    limit: "Negative limit",
    empty: "No wallet activity yet.",
    topupPlaceholder: "Top-up amount",
    topup: "Top up",
    limitPlaceholder: "Limit (0 or negative)",
    setLimit: "Save limit",
    hint: "Delivery charges deduct per completed trip; top-ups restore the balance. The negative limit is a manual guard — not enforced yet.",
    toastTopup: "Wallet topped up.",
    toastLimit: "Limit saved.",
    limitInvalid: "Limit must be 0 or a negative number.",
    failed: "Wallet update failed.",
  },
  clientLedger: {
    title: "Delivery fees due to the partner",
    balanceToPay: "Balance to pay",
    feesTotal: "Fees total",
    paidTotal: "Paid",
    empty: "Nothing due right now.",
    returnToStore: "Return to store",
    note: "Delivery fees the store covers (customer gets free delivery) and did not pay to the driver in cash — settled with the partner.",
    payTitle: "Record a payment from the store",
    payHint: "Record the amount you received from the store — it reduces the balance to pay.",
    payAmount: "Amount",
    payNote: "Note (optional)",
    payCurrent: "Currently due: {amount}",
    payAfter: "After payment: {amount}",
    paySubmit: "Record payment",
    payToast: "Payment of {amount} recorded.",
    paymentsTitle: "Recorded payments",
    owesPartner: "Client owes partner",
    partnerOwes: "Partner owes client",
    net: "Net",
  },
  walletV2: {
    title: "Partner wallet",
    hint: "Consolidated billing wallet — platform fees and delivery settlements only.",
    blockedBanner:
      "Dispatch is paused: the wallet is below its credit limit. Ask OELOX to top up to resume dispatch — store operations are not affected.",
    lowBanner:
      "Wallet balance is at or below the alert threshold — top up soon to avoid dispatch pauses.",
    balanceLabel: "Balance",
    statusHealthy: "Healthy",
    statusLow: "Low balance",
    statusBlocked: "Blocked",
    updatedAt: "Updated {time}",
    creditLimit: "Credit limit",
    creditLimitHint: "Dispatch blocks below this balance.",
    alertThreshold: "Alert threshold",
    alertThresholdHint: "Low-balance push alert trigger.",
    feeOnlyNote:
      "Wallets track the 250 IQD platform fee per completed order and delivery-fee settlements — food/order values never touch the wallet.",
    totalsLine: "Platform usage {fees} · Topped up {topups} · {count} entries",
    transactionsTitle: "Transactions",
    empty: "No wallet activity yet.",
    colDate: "Date",
    colType: "Type",
    colDescription: "Description",
    colAmount: "Amount",
    colBalanceAfter: "Balance after",
    fleetTitle: "Fleet driver wallets",
    fleetTotals: "To hand over: {cash} · Payout due: {due}",
    fleetEmpty: "No fleet drivers yet.",
    fleetNote:
      "Driver wallets: + means the driver owes cash; − means a payout is due. Settle from the driver panel.",
    colDriver: "Driver",
    colBalance: "Balance",
    colCashHeld: "Cash held",
    colPayoutDue: "Payout due",
    txType: {
      topup: "Top-up",
      platform_fee: "Platform fee",
      adjustment: "Adjustment",
      refund: "Refund",
      cash_collected: "Cash collected",
      trip_earnings: "Trip earnings",
      cash_handover: "Cash handover",
      partner_margin: "Partner margin",
      payout: "Payout",
      order_value_collected: "Order value collected",
      rejection_refund: "Rejected-order refund",
      rejection_fee_keep: "Rejected-order driver fee",
      other: "Entry",
    },
    txDesc: {
      cashCollected: "Cash collected at door",
      orderValueCollected: "Order value collected at door",
      tripEarnings: "Earned delivery fee",
      partnerShareAuto: "Partner delivery share (auto)",
      marginDeducted: "Partner margin — deducted from balance",
      feeReceived: "Delivery fee received",
      platformFee: "Platform usage fee",
      cashHandedOver: "Cash handed over",
      payoutToDriver: "Payout to driver",
      topup: "Wallet top-up",
      rejectionRefund: "Rejected order — refund of paid-at-cashier order value",
      rejectionFeeKeep: "Rejected order — driver fee kept by partner decision",
    },
    driverOwes: "Driver owes cash to hand over",
    payoutDue: "Payout due to the driver",
    settled: "Settled — nothing owed",
    cashOnHand: "Cash on hand",
    settlementsTitle: "Recent trip settlements",
    colTripDate: "Date",
    colTripFee: "Delivery fee",
    colTripEarnings: "Earnings",
    colTripCash: "Cash",
    colTripStatus: "Order",
    settleTitle: "Cash movement with driver",
    settleHandover: "Cash handover",
    settlePayout: "Payout",
    settleAmount: "Amount",
    settleNote: "Reason (optional)",
    settleSubmit: "Record",
    settleTopup: "Add credit",
    settleHint:
      "Handover: the driver returns collected cash (max cash held). Payout: pay what is owed (max payout due).",
    topupHint:
      "Add credit to the driver's wallet — recorded as an adjustment on the net balance (use for advances/float).",
    toastSettled: "Settlement recorded.",
    toastToppedUp: "Credit added to the driver wallet.",
    stDriverOwes: "Driver owes partner",
    stPartnerOwes: "Partner owes driver",
    stNet: "Net balance",
    stWalletBalance: "Wallet balance",
    stTripsCaption: "Completed trips",
    stIncomeCaption: "His share of every trip",
    stBalanceCaption: "Top-up balance — partner margin deducted each trip",
    stBalanceNegative: "Negative balance — add balance to settle it",
    addBalanceTitle: "Add balance",
    addBalanceHint:
      "The amount is added to the driver's advance balance — the partner margin is deducted automatically on every trip.",
    addBalanceSubmit: "Add balance",
    addBalanceCurrent: "Current: {amount}",
    addBalanceAfter: "After adding: {amount}",
    addBalanceNegative:
      "It will show as negative (owed for settlement) until balance is added.",
    moveTake: "Take from driver",
    moveGive: "Give to driver",
    moveTakeHint: "You receive cash from the driver (he currently holds {amount}).",
    moveTakeDue: "To receive: {amount}",
    moveGiveDue: "To pay: {amount}",
    takeScopeLabel: "What are you receiving?",
    scopeNetDesc: "All remaining cash he owes",
    scopeDeliveryDesc: "Collected delivery margins",
    scopeOrderDesc: "Collected order values (cash)",
    givePayoutDesc: "Pay the driver his remaining dues",
    moveAmountDue: "Due now: {amount} — editable",
    moveFillFull: "Full amount",
    moveRemainCash: "The driver keeps {amount} in hand after this receipt.",
    moveRemainDue: "Remaining due after this payment: {amount}",
    moveTopupPreview: "Adds {amount} credit to the driver wallet (advance/float).",
    movePreviewHint: "Enter an amount to preview the effect.",
    giveReasonLabel: "What is the payment for?",
    givePayout: "Settle what's owed",
    givePayoutHint: "Pay the driver his remaining dues (currently {amount}).",
    giveTopup: "Add credit (advance)",
    giveTopupHint: "Adds credit on top of what the driver holds — for float/advances.",
    moveSubmitTake: "Record receipt",
    moveSubmitGive: "Record payment",
    toastReceived: "Received {amount} from the driver.",
    toastPaid: "Paid {amount} to the driver.",
    toastCredited: "Added {amount} credit to the driver wallet.",
    duesWarning:
      "Unsettled wallet dues — settle the balance before this client is transferred under a partner.",
    consolidatedNote: "Billing runs through your consolidated partner wallet.",
    breakdownTitle: "Account breakdown",
    bdTrips: "Total trips",
    bdIncome: "Driver income",
    bdOweDelivery: "Driver owes partner (delivery)",
    bdOweOrder: "Driver owes partner (order value)",
    bdPartnerOwes: "Partner owes driver",
    bdNote: "Income stays with the contractor; the owes fields show what remains after scoped handovers (delivery / order value). Handover and payout settle the net balance.",
    scopeLabel: "Apply to",
    scopeNet: "Net (all)",
    scopeDelivery: "Delivery margin",
    scopeOrder: "Order value",
    scopeHint: "Optional: clear the delivery margin or the collected order values instead of the whole balance.",
  },
  fees: {
    type: "Fee type",
    amount: "Fee amount",
    settlement: "Settlement",
    cashTarget: "Cash settlement",
    perOrder: "Per order",
    percentage: "Percentage",
    instant: "Instant to wallet",
    credit: "Credit (collect later)",
    targetBusiness: "Settle with business",
    targetPartner: "Settle with partner",
    unknown: "Not configured",
  },
  ledger: {
    status: {
      pending: "Pending",
      settled: "Settled",
      instant: "Instant",
      voided: "Voided",
      other: "—",
    },
  },
  payment: {
    prepaid: "Prepaid",
    cod: "COD",
    cashier: "Pay at cashier",
    unknown: "Unknown",
  },
  responsibility: {
    customer: "Customer",
    client: "Client",
    free: "Free delivery",
    oelox: "OELOX",
    other: "Other",
  },
  clientDrivers: {
    title: "Driver activity",
    sourceNote:
      "From your fleet's deliveries to this client — based on orders history only.",
    colDriver: "Driver",
    colVehicle: "Vehicle",
    colTrips: "Trips",
    colKm: "KM",
    colCost: "Driver cost",
    colLastTrip: "Last trip",
    empty: "No driver trips in this range.",
  },
  analytics: {
    eyebrow: "Insights",
    title: "Analytics",
    subtitle:
      "Orders, delivery income, service charges and collections for the selected period.",
    allClients: "All clients",
    all: "All",
    clientFilter: "Filter by client",
    fulfillmentFilter: "Filter by type",
    loadError: "Could not load analytics. Refresh and try again.",
    kpiOrders: "Orders",
    kpiDeliveryIncome: "Delivery income",
    fromCustomers: "Customers",
    fromClients: "Clients",
    kpiClientCharges: "Service charges",
    kpiCod: "Cash on delivery",
    kpiOutstanding: "Outstanding",
    pendingAdded: "Added in range: {amount}",
    kpiWallet: "Wallet balance",
    kpiAvgFee: "Avg delivery fee",
    avgKm: "Avg km",
    avgPrep: "Avg prep (min)",
    cancelledWithFee: "{count} cancelled with fee",
    ordersPerDay: "Orders per day",
    incomePerDay: "Delivery income per day",
    byClient: "By client",
    byDriver: "By driver",
    empty: "No data in this range.",
    colClient: "Client",
    colOrders: "Orders",
    colCompleted: "Completed",
    colCancelled: "Cancelled",
    colDeliveryFees: "Delivery fees",
    colCharges: "Service charges",
    colOutstanding: "Outstanding",
    colDriver: "Driver",
    colTrips: "Trips",
    colKm: "KM",
    colLastTrip: "Last trip",
    colDriverCost: "Driver cost",
    profitabilityTitle: "Profitability (this range)",
    profitRevenue: "Revenue (estimate)",
    profitDriverCosts: "Driver costs",
    profitOelox: "OELOX trip charges",
    profitEstimated: "Estimated profit",
    profitMarginValue: "{pct}% margin",
    profitSettledTrips: "{count} settled trips",
    profitVoided: "Voided: {amount}",
    profitNoRates:
      "Set the per-trip driver pay in Settings → Dispatch (or per driver) to include driver costs.",
    pendingNote:
      "Driver costs use the saved per-trip pay; OELOX charges come from trip settlements.",
  },
  settings: {
    eyebrow: "Workspace",
    title: "Settings",
    subtitle: "Partner profile, dispatch mode, preferences, and system status.",
    segmentsAria: "Settings sections",
    loadError: "Could not load partner settings. Refresh and try again.",
    languageCardTitle: "Language",
    languageCardText: "Arabic, Kurdish, or English for this partner workspace.",
    tabs: {
      profile: "Profile",
      dispatch: "Dispatch",
      fees: "Delivery fees",
      preferences: "Preferences",
      system: "System",
    },
    fees: {
      title: "Delivery fees",
      hint: "Distance bands that price deliveries for linked clients. The AI and the client apps read these same rates.",
      add: "Add band",
      save: "Save fees",
      saved: "Delivery fees saved",
      saveFailed: "Could not save delivery fees.",
      empty: "No bands yet — add the first one.",
      minKm: "Min km",
      maxKm: "Max km",
      fee: "Fee",
      driverAmount: "Driver pay",
      margin: "Margin",
      remove: "Remove band",
      calculator: "Fee calculator",
      calculatorHint: "Slide a distance to preview the fee.",
      testDistance: "Test distance: {distance}",
      noBand: "No covering band.",
      result: "Fee: {fee}",
      errors: {
        invalid: "Enter valid numbers.",
        range: "Max must be greater than min.",
        fee: "Fee must be greater than 0.",
        driver: "Driver pay must be a valid amount.",
        driverOverFee: "Driver pay cannot exceed the customer fee.",
        overlap: "Bands overlap — adjust the distances.",
      },
    },
    profile: {
      title: "Partner profile",
      hint: "Business identity for this partner account. Org name and contact are provisioned centrally.",
      logo: "Logo",
      logoHint: "Only branding field writable via existing partner profile RPC.",
      changeLogo: "Change logo",
      uploading: "Uploading…",
      businessInfo: "Business info",
      businessHint: "Read-only from partner profile.",
      name: "Partner name",
      slug: "Slug",
      status: "Status",
      contactEmail: "Contact email",
      displayName: "Your display name",
      role: "Your role",
      toastSaved: "Logo updated.",
    },
    dispatch: {
      title: "Dispatch settings",
      hint: "Configuration flags only — does not change assignment logic in this UI.",
      modeTitle: "Dispatch mode",
      modeHint: "Manual assign vs partner fleet auto-broadcast.",
      modeAria: "Dispatch mode",
      manualTitle: "Manual dispatch",
      manualHint: "Accept and assign partner drivers yourself.",
      bypassTitle: "Bypass (auto broadcast)",
      bypassHint: "Auto-accept and broadcast to active partner drivers.",
      fallbackTitle: "OELOX fallback",
      fallbackHint: "Allow requesting OELOX drivers when partner fleet cannot cover.",
      fallbackToggle: "Auto fallback (OELOX)",
      fallbackDesc: "Config flag only. Existing order actions still enforce backend rules.",
      driverModeTitle: "Default driver mode",
      driverModeHint: "Applied to new drivers and drivers without their own mode.",
      driverModeOwnHint: "The partner keeps the full delivery fee.",
      driverModePaidHint: "The driver's fee is tracked in the driver wallet.",
      payTitle: "Driver pay per trip",
      payHint:
        "Default amount paid to a driver for each completed delivery trip (IQD). Each driver can override it.",
      payLabel: "Default pay per trip (IQD)",
      payPlaceholder: "e.g. 2000",
      paySave: "Save pay rate",
      paySaved: "Default driver pay updated.",
      payDriverHint:
        "Used when a driver has no per-driver pay set. The rate is frozen on each trip when it completes.",
      toastSaved: "Dispatch settings saved.",
    },
    preferences: {
      title: "Operational preferences",
      hint: "UI-only preferences stored in this browser. No backend writes.",
      stageTitle: "Default order stage focus",
      stageHint: "Display preference only — does not change order pipeline behavior.",
      stageAll: "All stages",
      stagePending: "Pending",
      stageProcessing: "Processing",
      stageReady: "Ready",
      notifyTitle: "Notifications",
      notifyHint: "Local toggles for future browser alerts — not wired to push yet.",
      notifyOrders: "New orders",
      notifyOrdersDesc: "Highlight when new partner orders arrive.",
      notifyDrivers: "Driver updates",
      notifyDriversDesc: "Driver online / assignment status changes.",
      notifyFallback: "OELOX fallback alerts",
      notifyFallbackDesc: "When fallback request is used or unavailable.",
      toastSaved: "Preferences saved on this device.",
    },
    system: {
      title: "System info",
      hint: "Read-only operational snapshot for this partner.",
      partnerId: "Partner ID",
      partnerStatus: "Partner status",
      activeClients: "Active clients",
      clientsValue: "{active} active / {total} total",
      activeDrivers: "Active drivers",
      driversValue: "{fleet} fleet / {online} online",
      dispatchMode: "Dispatch mode",
      bypassLabel: "Bypass (auto broadcast)",
      manualLabel: "Manual",
      fallback: "OELOX fallback",
      driverMode: "Default driver mode",
      oeloxTripFee: "OELOX fee per trip",
      oeloxBalance: "OELOX wallet balance",
      oeloxLimit: "OELOX negative limit",
      oeloxWalletNote:
        "OELOX deducts the per-trip fee from this wallet; top-ups are handled by OELOX support.",
    },
  },
  accounts: {
    eyebrow: "Account credentials",
    username: "Username",
    password: "Password",
    initialPassword: "Initial password",
    newPassword: "New password",
    passwordPlaceholder: "At least 8 characters",
    passwordMin: "Use at least 8 characters.",
    generate: "Generate",
    show: "Show password",
    hide: "Hide password",
    shownOnce:
      "The password is shown only now. It is stored as a secure hash and can never be displayed again — use Reset password if it is lost.",
    done: "Done, I saved it",
    checking: "Checking…",
    available: "Available",
    resetTitle: "Reset password",
    resetHint:
      "Choose a new password for this account. The current password cannot be recovered; the new one is shown once.",
    resetCta: "Set password",
    resetAction: "Reset password",
    usernameLocked: "Locked — the login username cannot be changed after activation.",
  },
  errors: {
    walletDuesUnsettled:
      "The client wallet has unsettled dues — settle the balance first.",
    walletCashExceeds: "The amount exceeds the cash the driver is holding.",
    walletPayoutExceeds: "The amount exceeds the payout due.",
    walletScopeExceeds: "The amount exceeds this component's outstanding.",
    walletScopeInvalid: "Invalid handover scope.",
    walletKindInvalid: "Invalid settlement kind.",
    partnerClientNotFound: "That client is not linked to your account.",
    requestFailed: "Request failed.",
    usernameTaken: "Username already taken. Try another one.",
    usernameInvalid:
      "Invalid username. Use 3–30 chars: a-z, 0-9, dash only.",
    clientNotFound: "Client not found for this partner.",
    forbidden: "You don't have permission for this action.",
    somethingWrong: "Something went wrong.",
    roleCannotChange: "Your role cannot change these settings.",
    partnerAccessDenied: "Partner access denied.",
    invalidDispatchMode: "Invalid dispatch mode.",
    fallbackDisabled: "OELOX fallback is disabled for this partner.",
    driversAvailable: "Partner drivers are available — assign from your fleet.",
    noOeloxDrivers: "No OELOX drivers available right now.",
    acceptFirst: "Accept the order before assigning a driver.",
    walletBelowLimit:
      "Wallet is below its credit limit — dispatch is paused. Ask OELOX to top up the wallet to resume.",
    noDispatchAuthority: "No dispatch authority for this business.",
    alreadyAssigned: "Order is already assigned to another driver.",
    driverFull:
      "Driver reached the limit — max 2 orders at once (same-store orders are unlimited).",
    groupRound:
      "This order is part of one round — accept / reject / dispatch the whole round from the group card (mobile app).",
    alreadyAccepted: "Order is already accepted.",
    orderClosed: "Order is already closed.",
    driverNotInFleet: "That driver is not in your partner fleet.",
    nameRequired: "Driver name is required.",
    phoneInvalid: "Enter a valid phone number.",
    phoneTaken: "That phone number is already in use.",
    driverOtherPartner: "This driver already belongs to another partner.",
    driverInactive: "This driver is not active.",
    driverActiveOrders: "The driver has live deliveries — finish or reassign them first.",
    vehicleInvalid: "Invalid vehicle type.",
    driverNotFound: "Driver not found in your fleet.",
    driverModeInvalid: "Invalid driver mode.",
    amountInvalid: "Enter a non-zero amount.",
    amountTooLarge: "Amount is too large.",
    invalidTripFee: "Enter a valid amount (0 or more).",
    slugTaken: "That public link is already used by another store.",
    slugInvalid:
      "Invalid slug. Use 3–60 lowercase letters, digits or single dashes.",
    passwordTooWeak: "Use a password with at least 8 characters.",
    emailInvalid: "Enter a valid email address.",
    credentialsUnavailable:
      "This account has no login yet — finish activation first.",
    requiredFields: "Fill in all required fields.",
    notFound: "Account not found for this partner.",
    rateLimited: "Too many attempts. Try again later.",
  },
} as const;

export default en;
